import { useLazyQuery, useQuery } from "@apollo/client";
import cn from "classnames";
import { getCookie, setCookie } from "cookies-next";
import compareAsc from "date-fns/compareAsc";
import dateFormat from "date-fns/format";
import isSameDay from "date-fns/isSameDay";
import isValid from "date-fns/isValid";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";

import { BUS_STOPS } from "@api/queries/bus-stops";
import { POPULAR_DESTINATIONS } from "@api/queries/search";
import { useIsMounted } from "@hooks/useIsMounted";
import { useMediaQuery } from "@hooks/useMediaQuery";
import { useStore } from "@hooks/useZustandProvider";
import i from "@styles/icons.module.scss";
import DatePicker from "@ui-kit/DatePicker/DatePicker";
import Popper from "@ui-kit/Popper/Popper";
import SearchInput from "@ui-kit/SearchInput/SearchInput";
import { getCorrectDate, localizedDate } from "@utils/utils";

import SearchField from "../SearchField/SearchField";

import PassengersField from "./components/PassengersField/PassengersField";
import { parseDepartDate } from "./helpers";
import s from "./Search.module.scss";

const Button = dynamic(() => import("@ui-kit/Button/Button"), {
  ssr: false,
});

const Search = ({
  className,
  ButtonSearch,
  inputsClassName,
  prefill = {},
  onReturnDateChange,
  passengersFieldRef: externalPassengersFieldRef,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const isMounted = useIsMounted();
  const { locale, query, pathname, ...router } = useRouter();
  const internalPassengersFieldRef = useRef(null);
  const passengersFieldRef =
    externalPassengersFieldRef || internalPassengersFieldRef;

  const {
    search,
    modals,
    updateModals,
    changeCurrency,
    swapDirections,
    updatePassengers,
    updateSearch,
    updateDestination,
    validatePassengers,
    app,
  } = useStore();

  const {
    promocode,
    departDate,
    returnDate,
    from,
    to,
    field,
    passengers,
    passengersCount,
    isSearchValid,
    preschoolChildWarning,
  } = search;

  const {
    isOpenSearchDestinationMobileModal,
    isOpenSearchPassengersMobileModal,
  } = modals;

  const toggleSearchDestinationMobileModal = (field) => {
    updateSearch({
      field,
    });
    updateModals({
      isOpenSearchDestinationMobileModal: !isOpenSearchDestinationMobileModal,
    });
  };

  const toggleSearchPassengersMobileModal = () => {
    updateModals({
      isOpenSearchPassengersMobileModal: !isOpenSearchPassengersMobileModal,
    });
  };

  const { currencyName } = app;

  const [getStops, { loading: stopsLoading }] = useLazyQuery(BUS_STOPS, {
    fetchPolicy: "cache-first",
  });

  const { data: popularDestinations } = useQuery(POPULAR_DESTINATIONS, {
    variables: {
      lang: locale,
    },
  });

  const updateDate = (date) => {
    const isDeparture = Object.keys(date)[0] === "departDate";
    const isResetReturnDate =
      isDeparture && compareAsc(date.departDate, returnDate) === 1;

    updateSearch({
      ...(isResetReturnDate
        ? {
            ...date,
            returnDate: "",
          }
        : date),
    });
  };

  const handleReturnDateChange = (date) => {
    const nextReturnDate = date || "";

    if (
      (!nextReturnDate && !returnDate) ||
      (nextReturnDate &&
        returnDate &&
        isSameDay(getCorrectDate(nextReturnDate), getCorrectDate(returnDate)))
    ) {
      return;
    }

    updateDate({ returnDate: nextReturnDate });

    if (
      onReturnDateChange &&
      from.BusStopId &&
      to.BusStopId &&
      isValid(new Date(departDate)) &&
      passengersCount > 0
    ) {
      pushAnaliticsToGTM(nextReturnDate);
      onReturnDateChange(nextReturnDate);
    }
  };

  const selectItem = (search, field) => {
    updateSearch({
      [field]: {
        search: search.BusStopName,
        BusStopId: search.BusStopId,
        CityName: search.CityName,
        CountryName: search.CountryName,
      },
    });
  };

  const handleUpdateDestination = (e, fieldName) => {
    updateDestination(
      {
        search: e.target.value,
        BusStopId: "",
      },
      fieldName,
    );
  };

  const handleClearDestination = (field) => {
    updateDestination(
      {
        search: "",
        BusStopId: "",
      },
      field,
    );
  };

  const pushAnaliticsToGTM = (searchReturnDate = returnDate) => {
    if (window.dataLayer) {
      dataLayer.push({
        tripType: searchReturnDate ? "return-trip" : "one-way",
        departureStopId: from.BusStopId,
        destinationStopId: to.BusStopId,
        currencyCode: currencyName,
        route: `${from.CityName}-${to.CityName}`,
        promocode: promocode || null,
        departureDestination: `${from.CountryName}-${to.CountryName}`,
        stage: "search",
        event: "search",
        departDate: dateFormat(new Date(departDate), "yyyy-MM-dd"),
        ...(searchReturnDate
          ? {
              returnDate: dateFormat(new Date(searchReturnDate), "yyyy-MM-dd"),
            }
          : {}),
      });
    }
  };

  const handleSearch = () => {
    pushAnaliticsToGTM();

    const affiliateId = query.affiliateId || getCookie("affiliateId");

    router.push({
      pathname: "/tickets/search",
      query: {
        promocode,
        departDate: dateFormat(new Date(departDate), "yyyy-MM-dd"),
        ...(returnDate
          ? {
              returnDate: dateFormat(new Date(returnDate), "yyyy-MM-dd"),
            }
          : {}),
        currency: currencyName,
        fromBusStopId: from.BusStopId,
        toBusStopId: to.BusStopId,
        adult: passengers.adult,
        senior: passengers.senior,
        youth: passengers.youth,
        pupil: passengers.pupil,
        child: passengers.child,
        affiliateId,
      },
      shallow: true,
    });
  };

  const handleSearchButtonClick = () => {
    if (isSearchValid) {
      handleSearch();
    } else if (passengersCount === 0) {
      if (isMobile) {
        toggleSearchPassengersMobileModal();
      } else {
        passengersFieldRef.current?.openPopper();
      }
    }
  };

  const checkValidSearch = useCallback(() => {
    const isSearchValid =
      !!from.BusStopId && !!to.BusStopId && !!departDate && passengersCount > 0;

    updateSearch({
      isSearchValid,
    });
  }, [from.BusStopId, to.BusStopId, departDate, passengersCount]);

  useEffect(() => {
    checkValidSearch();
  }, [checkValidSearch]);

  useEffect(() => {
    const { promocode = "", fromBusStopId = "", toBusStopId = "" } = query;

    let {
      departDate = new Date(),
      returnDate,
      adult,
      senior,
      youth,
      pupil,
      child,
    } = query;

    // Parse passenger counts
    const parsedPassengers = {
      adult: Math.max(0, Number(adult) || 0),
      senior: Math.max(0, Number(senior) || 0),
      youth: Math.max(0, Number(youth) || 0),
      pupil: Math.max(0, Number(pupil) || 0),
      child: Math.max(0, Number(child) || 0),
    };

    // Fallback for old URLs that only have "passengers" parameter
    const totalFromParams = Object.values(parsedPassengers).reduce(
      (sum, count) => sum + count,
      0,
    );

    if (totalFromParams === 0 && +adult > 0) {
      parsedPassengers.adult = +adult;
    }
    // TODO: for fallback for old URLs
    // if (totalFromParams === 0 && +passengers > 0) {
    //   parsedPassengers.adult = +passengers;
    // }

    // Ensure at least 1 passenger
    // if (totalFromParams === 0) {
    //   parsedPassengers.adult = 0;
    // }

    const passengersCount = Object.values(parsedPassengers).reduce(
      (sum, count) => sum + count,
      0,
    );

    returnDate = isValid(new Date(returnDate)) ? returnDate : "";
    departDate = parseDepartDate(departDate);

    const from = fromBusStopId ? { BusStopId: fromBusStopId } : {};
    const to = toBusStopId ? { BusStopId: toBusStopId } : {};

    updateSearch({
      promocode,
      returnDate,
      departDate,
      from,
      to,
      passengersCount,
      passengers: parsedPassengers,
    });

    setTimeout(() => {
      validatePassengers();
    }, 0);
  }, []);

  useEffect(() => {
    if (query?.currency?.length === 3 && currencyName !== query.currency) {
      changeCurrency(query.currency);
    }
  }, [query.currency]);

  useEffect(() => {
    if (
      isMounted &&
      query?.currency?.length === 3 &&
      currencyName !== query.currency
    ) {
      router.replace({
        pathname,
        query: {
          ...query,
          currency: currencyName,
        },
      });
    }
  }, [currencyName]);

  const getStop = (id, direction) => {
    if (id) {
      getStops({
        variables: {
          ids: [id],
          lang: locale,
        },
      }).then((data) => {
        if (data?.data?.busStops?.length) {
          const busStop = data.data.busStops[0];
          updateSearch({
            [direction]: {
              BusStopId: id,
              CountryName: busStop.CountryName,
              CityName: busStop.Address.CityName,
              search: busStop.BusStopName,
              isWheelchairAccessible: busStop.IsWheelchairAccessible,
            },
          });
        }
      });
    } else {
      updateSearch({
        [direction]: {
          BusStopId: "",
          CountryName: "",
          CityName: "",
          search: "",
          isWheelchairAccessible: false,
        },
      });
    }
  };

  useEffect(() => {
    if (isMounted) {
      const fromId = prefill.from || +query.fromBusStopId;
      const toId = prefill.to || +query.toBusStopId;
      getStop(fromId, "from");
      getStop(toId, "to");
    }
  }, [
    isMounted,
    locale,
    query.fromBusStopId,
    query.toBusStopId,
    prefill.from,
    prefill.to,
  ]);

  useEffect(() => {
    if (query.affiliateId) {
      setCookie("affiliateId", query.affiliateId);
    }
  }, [query.affiliateId]);

  return (
    <div className={cn(s.search, className)}>
      <div className={cn(s.inputs, inputsClassName)}>
        <div
          className={cn(
            s.input,
            s.direction,
            s.from,
            field === "from" && s.active,
          )}
        >
          <SearchField
            modalProps={{
              search: from.search || "",
              selectedStop: to.BusStopId,
              isOpen: field === "from" && isOpenSearchDestinationMobileModal,
              toggleModal: () => toggleSearchDestinationMobileModal("from"),
              modalTitle: "from",
              selectItem: (item) => selectItem(item, "from"),
              updateSearch: (e) => handleUpdateDestination(e, "from"),
              popularDestinations: popularDestinations?.popularDestinations,
            }}
            triggerElement={
              <SearchInput
                onClick={() => toggleSearchDestinationMobileModal("from")}
                value={from.search}
                label={t("from")}
                placeholder={t("try riga")}
                onChange={(e) => handleUpdateDestination(e, "from")}
                loading={stopsLoading}
                readOnly={isMobile}
                // data-title={
                //   !isMobile && isOpenSugg && field === "from" ? "" : from.search
                // }
              />
            }
            popperContentProps={{
              selectedStop: to.BusStopId,
              selectItem: (item) => selectItem(item, "from"),
              search: from.search,
              popularDestinations: popularDestinations?.popularDestinations,
            }}
          />
          {(to.BusStopId || from.BusStopId) && (
            <div className={s.swap} onClick={swapDirections}>
              <icon className={i.exchange} />
            </div>
          )}
        </div>
        <SearchField
          className={cn(s.input, s.direction, s.to, field === "to" && s.active)}
          modalProps={{
            search: to.search || "",
            selectedStop: from.BusStopId,
            isOpen: field === "to" && isOpenSearchDestinationMobileModal,
            toggleModal: () => toggleSearchDestinationMobileModal("to"),
            modalTitle: "to",
            selectItem: (item) => selectItem(item, "to"),
            updateSearch: (e) => handleUpdateDestination(e, "to"),
            popularDestinations: popularDestinations?.popularDestinations,
          }}
          triggerElement={
            <SearchInput
              onClick={() => toggleSearchDestinationMobileModal("to")}
              value={to.search}
              label={t("to")}
              placeholder={t("try riga")}
              onChange={(e) => handleUpdateDestination(e, "to")}
              loading={stopsLoading}
              readOnly={isMobile}
              // data-title={
              //   !isMobile && isOpenSugg && field === "from" ? "" : from.search
              // }
            />
          }
          popperContentProps={{
            selectedStop: from.BusStopId,
            selectItem: (item) => selectItem(item, "to"),
            search: to.search,
            popularDestinations: popularDestinations?.popularDestinations,
          }}
        />
        <Popper
          className={cn(s.input, s.datePicker)}
          onClick={() => {
            updateSearch({ field: "" }); // remove active field for correct work z-index
          }}
          triggerElement={
            <SearchInput
              value={localizedDate(departDate, "d MMMM, eee", locale)}
              label={t("depart")}
              placeholder={t("pick the date")}
              readOnly
            />
          }
          popperContent={
            <DatePicker
              defaultMonth={new Date(departDate)}
              selected={departDate}
              onSelect={(departDate) => {
                updateDate({ departDate: departDate || new Date() });
              }}
              disabled={[
                {
                  before: new Date(),
                },
              ]}
            />
          }
        />
        <Popper
          className={cn(s.input, s.datePicker)}
          onClick={() => {
            updateSearch({ field: "" }); // remove active field for correct work z-index
          }}
          triggerElement={
            <SearchInput
              value={
                returnDate
                  ? localizedDate(returnDate, "d MMMM, eee", locale)
                  : ""
              }
              label={t("return")}
              placeholder={t("pick the date")}
              onClearButton={() => handleReturnDateChange("")}
              hasClearButton
              readOnly
            />
          }
          popperContent={
            <DatePicker
              defaultMonth={returnDate ? new Date(departDate) : ""}
              selected={returnDate}
              onSelect={handleReturnDateChange}
              disabled={[
                {
                  before: departDate ? new Date(departDate) : new Date(),
                },
              ]}
              fromDate={departDate}
            />
          }
        />
        <PassengersField
          ref={passengersFieldRef}
          className={cn(s.input, s.passengers)}
          modalProps={{
            passengersCount,
            isOpen: isOpenSearchPassengersMobileModal,
            passengers,
            toggleModal: toggleSearchPassengersMobileModal,
            updatePassengers,
            preschoolChildWarning,
          }}
          triggerElement={
            <SearchInput
              onClick={toggleSearchPassengersMobileModal}
              value={Object.entries(passengers)
                .filter(([, count]) => count > 0)
                .map(
                  ([type, count]) =>
                    `${count} ${t(`passengers.type.${type}.title`, { count })}`,
                )
                .join(", ")}
              label={t("passengers")}
              icon="passenger"
              readOnly="readonly"
              placeholder={t("add")}
            />
          }
          popperContentProps={{
            passengersCount,
            updatePassengers,
            passengers,
            preschoolChildWarning,
          }}
        />
        <div className={cn(s.input, s.promocode)}>
          <SearchInput
            value={promocode}
            label={t("promocode")}
            placeholder={t("enter code")}
            hasClearButton
            onClearButton={() => {
              updateSearch({
                promocode: "",
              });
            }}
            onChange={(e) => {
              updateSearch({
                promocode: e.target.value,
              });
            }}
          />
        </div>
      </div>
      {ButtonSearch ? (
        <ButtonSearch
          disabled={!isSearchValid}
          pushAnaliticsToGTM={pushAnaliticsToGTM}
          passengersFieldRef={passengersFieldRef}
          passengersCount={passengersCount}
          toggleSearchPassengersMobileModal={toggleSearchPassengersMobileModal}
        />
      ) : (
        <Button
          disabled={!isSearchValid}
          canClickOnDisabled
          text={t("search")}
          size="l"
          onClick={handleSearchButtonClick}
        />
      )}
    </div>
  );
};

export default Search;
